import { createContext, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "../lib/api";

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();

  const checkAuth = async () => {
    try {
      const r = await api.get("/auth/me", { withCredentials: true });
      setUser(r.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = (redirectPath) => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // We append the post-login destination as a query param to the local callback URL.
    const callback = redirectPath
      ? `${window.location.origin}/auth/callback?next=${encodeURIComponent(redirectPath)}`
      : window.location.origin + "/auth/callback";
    // prompt=select_account skips Emergent's slow intermediate selection and goes
    // straight to Google's account picker — feels much faster.
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(callback)}&prompt=select_account`;
  };

  // Manual email/password login — returns the user on success, throws on error
  const loginEmail = async (email, password) => {
    const r = await api.post("/customers/login", { email, password }, { withCredentials: true });
    setUser(r.data.user);
    return r.data.user;
  };

  const registerEmail = async ({ name, email, phone, cpf, password, privacyPolicyAccepted }) => {
    const r = await api.post(
      "/customers/register",
      {
        name,
        email,
        phone,
        cpf,
        password,
        privacy_policy_accepted: !!privacyPolicyAccepted,
      },
      { withCredentials: true },
    );
    return r.data;
  };

  const verifyEmail = async (token) => {
    const r = await api.post("/customers/verify-email", { token }, { withCredentials: true });
    setUser(r.data.user);
    return r.data.user;
  };

  const resendVerification = (email) => api.post("/customers/resend-verification", { email });
  const forgotPassword = (email) => api.post("/customers/forgot-password", { email });
  const resetPassword = (token, password) => api.post("/customers/reset-password", { token, password });

  const logout = async () => {
    try { await api.post("/customers/logout", {}, { withCredentials: true }); } catch { /* ignore */ }
    try { await api.post("/auth/logout", {}, { withCredentials: true }); } catch { /* ignore */ }
    setUser(null);
    navigate("/");
  };

  return (
    <AuthContext.Provider value={{
      user, loading,
      login, loginEmail, registerEmail, verifyEmail,
      resendVerification, forgotPassword, resetPassword,
      logout, checkAuth,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
